import { DeleteOutlined, UploadOutlined, DownloadOutlined } from "@ant-design/icons";
import { Button, Col, Divider, Input, Row, Select, Typography, DatePicker, Checkbox, AutoComplete, Upload, message } from "antd";
import React, { useState, useEffect } from "react";
import styled, { createGlobalStyle } from "styled-components";
import dayjs, { Dayjs } from 'dayjs';
import { CheckboxField } from '../../components/CheckboxField';
import { createStandard } from '../../api/chemicalRegistry';
import { getChemicalNames, getPrecursorChemicalNames } from '../../api/chemicals';
import { useLaboratoryId } from '../../hooks/useLaboratoryId';
import type { SelectProps } from 'antd/es/select';
import type { DefaultOptionType } from 'antd/es/select';
import type { DatePickerProps } from 'antd';
import type { AutoCompleteProps } from 'antd/es/auto-complete';
import { getCustomColumns, CustomColumn } from '../../api/customColumns';
import type { UploadProps, UploadFile } from 'antd/es/upload/interface';
import MultipleStandardSamplesAdd from '../../components/MultipleStandardSamplesAdd';

const { Option } = Select;
const { Text } = Typography;

const ScreenContainer = styled.div`
  width: 400px;
  height: 500px;
  background: white;
  border: 1px solid #d9d9d9;
  border-radius: 8px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.15);
  display: flex;
  flex-direction: column;
`;

const ScreenTitle = styled(Typography.Title)`
  margin: 16px !important;
  font-size: 16px !important;
`;

const ScrollContent = styled.div`
  flex: 1;
  overflow-y: auto;
  padding: 0 16px;

  &::-webkit-scrollbar {
    width: 4px;
  }

  &::-webkit-scrollbar-track {
    background: #f0f0f0;
  }

  &::-webkit-scrollbar-thumb {
    background: #ccc;
    border-radius: 2px;
  }
`;

const BottomButtons = styled.div`
  padding: 16px;
`;

const StyledInput = styled(Input)`
  height: 40px;
  background-color: #fafafa;
  border-radius: 4px;

  /* Скрываем стрелки для числовых полей */
  &[type='number'] {
    -moz-appearance: textfield;
    
    &::-webkit-outer-spin-button,
    &::-webkit-inner-spin-button {
      -webkit-appearance: none;
      margin: 0;
    }
  }
`;

const StyledSelect = styled(Select)`
  .ant-select-selector {
    height: 40px !important;
    padding: 4px 11px !important;
    background-color: #fafafa !important;
    border-radius: 4px !important;
    
    .ant-select-selection-search-input {
      height: 38px !important;
    }
    
    .ant-select-selection-item {
      line-height: 32px !important;
    }
  }

  // Центрируем иконки по вертикали
  .ant-select-arrow,
  .ant-select-clear {
    top: 50% !important;
    margin-top: -3px !important;
  }
`;

const StyledDatePicker = styled(DatePicker)`
  height: 40px;
  background-color: #fafafa;
  border-radius: 4px;

  .ant-picker-input > input {
    background-color: #fafafa;
  }
`;

const StyledText = styled(Text)`
  color: #8c8c8c;
  font-weight: 500;
  font-size: 12px;
`;

const ButtonsRow = styled(Row)`
  display: flex;
  justify-content: center;
  gap: 8px;
  
  .ant-btn {
    height: 40px;
  }
`;

const HeaderContainer = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
`;

const StyledLink = styled(Typography.Link)`
  color: #8c8c8c !important;
  font-size: 14px;
  text-decoration: underline !important;
  margin-right: 20px;
`;

const StyledAutoComplete = styled(AutoComplete)<AutoCompleteProps<string, OptionType>>`
  .ant-select-selector {
    height: 40px !important;
    padding: 4px 11px !important;
    background-color: #fafafa !important;
    border-radius: 4px !important;
    
    .ant-select-selection-search-input {
      height: 38px !important;
    }
    
    .ant-select-selection-placeholder {
      line-height: 32px !important;
    }
  }
`;

// Добавляем пропсы для компонента
interface ScreenProps {
  onCancel: () => void;
  onSuccess?: () => void;
}

// Опции из Reagents.tsx
const standardTypes = [
    'ГСО 1234-56 Стандартный образец 1',
    'ОСО 7890-12 Стандартный образец 2',
    'СОП 3456-78 Стандартный образец 3',
].map(item => ({ label: item, value: item }));

const manufacturerOptions = [
    'ФГУП "ВНИИМ им. Д.И. Менделеева"',
    'ФГУП "УНИИМ"',
    'ООО "Химтест"',
].map(item => ({ label: item, value: item }));

const purposeOptions = [
    'Калибровка',
    'Поверка',
    'Контроль точности',
].map(item => ({ label: item, value: item }));

const indicatorOptions = [
    'Массовая доля железа',
    'Массовая доля углерода',
    'Массовая доля серы',
].map(item => ({ label: item, value: item }));

const notificationOptions = [
  'За 30 дней',
  'За 60 дней',
  'За 90 дней',
].map(item => ({ label: item, value: item }));

type OptionType = {
    value: string;
    label: string;
};

const Screen: React.FC<ScreenProps> = ({ onCancel, onSuccess }) => {
  const laboratoryId = useLaboratoryId();
  const [loading, setLoading] = useState(false);
  const [chemicalNames, setChemicalNames] = useState<string[]>([]);
  const [nameOptions, setNameOptions] = useState<OptionType[]>([]);
  const [precursorChemicalNames, setPrecursorChemicalNames] = useState<string[]>([]);
  
  // Состояния для полей формы
  const [name, setName] = useState('');
  const [manufacturer, setManufacturer] = useState('');
  const [purpose, setPurpose] = useState('');
  const [batchCode, setBatchCode] = useState('');
  const [packaging, setPackaging] = useState('');
  const [indicator, setIndicator] = useState('');
  const [certifiedValue, setCertifiedValue] = useState('');
  const [certifiedValueUnit, setCertifiedValueUnit] = useState('');
  const [errorValue, setErrorValue] = useState('');
  const [errorValueUnit, setErrorValueUnit] = useState('');
  const [additionalInfo, setAdditionalInfo] = useState('');
  const [normativeDocument, setNormativeDocument] = useState('');
  const [normativeDocumentFile, setNormativeDocumentFile] = useState<File | null>(null);
  const [expirationDate, setExpirationDate] = useState<dayjs.Dayjs | null>(null);
  const [expirationNotification, setExpirationNotification] = useState('');
  const [releaseDate, setReleaseDate] = useState<dayjs.Dayjs | null>(null);
  const [notes, setNotes] = useState('');
  const [isPrecursor, setIsPrecursor] = useState(false);
  const [minimumRemaining, setMinimumRemaining] = useState<string>('10');
  
  // Добавляем состояние для кастомных полей
  const [customColumns, setCustomColumns] = useState<CustomColumn[]>([]);
  const [customFieldValues, setCustomFieldValues] = useState<Record<string, any>>({});

  // Добавляем состояния для новых полей
  const [storageLocation, setStorageLocation] = useState('');
  const [storageConditions, setStorageConditions] = useState('');
  const [openedShelfLife, setOpenedShelfLife] = useState('');
  const [openedDate, setOpenedDate] = useState<dayjs.Dayjs | null>(null);
  const [used, setUsed] = useState('0');
  const [remaining, setRemaining] = useState('');

  const [isMultipleAddOpen, setIsMultipleAddOpen] = useState(false);

  useEffect(() => {
    const fetchChemicalNames = async () => {
      try {
        const names = await getChemicalNames();
        setChemicalNames(names);
        
        // Загружаем список прекурсоров
        const precursorNames = await getPrecursorChemicalNames();
        setPrecursorChemicalNames(precursorNames);
        
        // Объединяем оба списка в один для автокомплита без использования Set
        const combinedNames = [...names];
        // Добавляем только те прекурсоры, которых нет в основном списке
        precursorNames.forEach(name => {
          if (!combinedNames.includes(name)) {
            combinedNames.push(name);
          }
        });
        setNameOptions(combinedNames.map(name => ({ value: name, label: name })));
      } catch (error) {
        console.error('Error fetching chemical names:', error);
      }
    };

    fetchChemicalNames();
  }, []);
  
  // Добавляем эффект для загрузки кастомных колонок
  useEffect(() => {
    const fetchCustomColumns = async () => {
      if (!laboratoryId) return;
      
      try {
        const columns = await getCustomColumns('standard', Number(laboratoryId));
        setCustomColumns(columns);
      } catch (error) {
        console.error('Ошибка при загрузке кастомных колонок:', error);
      }
    };
    
    fetchCustomColumns();
  }, [laboratoryId]);

  const handleSearch = (searchText: string) => {
    // Объединяем списки химических веществ и прекурсоров без использования Set
    const allChemicals = [...chemicalNames];
    precursorChemicalNames.forEach(name => {
      if (!allChemicals.includes(name)) {
        allChemicals.push(name);
      }
    });
    
    const filteredOptions = allChemicals
      .filter(name => name.toLowerCase().includes(searchText.toLowerCase()))
      .map(name => ({ value: name, label: name }));
    setNameOptions(filteredOptions);
  };

  // Type-safe handlers for Select components
  const handleSelectChange = (setter: (value: string) => void) => 
      (value: unknown, _: DefaultOptionType | DefaultOptionType[]) => {
          setter(value as string);
      };

  // Обновленный обработчик для DatePicker
  const handleDateChange = (setter: (date: dayjs.Dayjs | null) => void) => 
      (value: any) => {
          setter(value);
      };
      
  // Добавляем обработчик для кастомных полей
  const handleCustomFieldChange = (key: string, value: any) => {
    setCustomFieldValues(prev => ({
      ...prev,
      [key]: value
    }));
  };

  // Добавляем обработчик для изменения наименования
  const handleNameChange = (value: string) => {
    // Убедимся, что value не undefined и не null
    const newValue = value || '';
    setName(newValue);
    
    // Проверка по списку прекурсоров из API
    const isPrecursorFromList = precursorChemicalNames.length > 0 && 
                                precursorChemicalNames.some(name => 
                                  name.toLowerCase() === newValue.toLowerCase());
    
    // Альтернативная проверка по ключевым словам в названии
    const lowercaseValue = newValue.toLowerCase();
    const containsPrecursorKeywords = lowercaseValue.includes('прекурсор') || 
                                      lowercaseValue.includes('наркотическ') ||
                                      lowercaseValue.includes('психотроп');
    
    // Устанавливаем флаг, если вещество в списке прекурсоров или содержит ключевые слова
    setIsPrecursor(isPrecursorFromList || containsPrecursorKeywords);
  };

  const handleSave = async () => {
    if (!laboratoryId) return;
    
    console.log('Saving with name:', name);
    
    // Валидация обязательных полей
    if (!name || !name.trim()) {
        message.error('Введите название стандартного образца');
        return;
    }

    try {
        setLoading(true);
        
        // Формируем данные для отправки
        const standardData = new FormData();

        // Создаем объект с данными
        const jsonData = {
            name: name.trim(),
            manufacturer: manufacturer || '',
            purpose: purpose || '',
            batch_code: batchCode || '',
            packaging: packaging || '',
            indicator: indicator || '',
            certified_value: certifiedValue || '',
            certified_value_unit: certifiedValueUnit || '',
            error_value: errorValue || '',
            error_value_unit: errorValueUnit || '',
            additional_info: additionalInfo || '',
            normative_document: normativeDocument || '',
            expiration_date: expirationDate?.format('YYYY-MM-DD') || '',
            expiration_notification: expirationNotification || '',
            release_date: releaseDate?.format('YYYY-MM-DD') || '',
            notes: notes || '',
            is_precursor: isPrecursor,
            minimum_remaining: parseFloat(minimumRemaining) || 10,
            storage_location: storageLocation || '',
            storage_conditions: storageConditions || '',
            opened_shelf_life: openedShelfLife ? parseInt(openedShelfLife) : null,
            opened_date: openedDate?.format('YYYY-MM-DD') || '',
            used: parseFloat(used) || 0,
            remaining: parseFloat(remaining) || 0,
            custom_columns: customFieldValues
        };

        console.log('=== DEBUG INFO ===');
        console.log('JSON Data:', jsonData);
        console.log('=================');

        // Добавляем JSON строку в FormData
        standardData.append('standard_data', JSON.stringify(jsonData));

        // Добавляем файл, если он есть
        if (normativeDocumentFile) {
            standardData.append('normative_document', normativeDocumentFile);
        }

        console.log('=== FORM DATA ===');
        Array.from(standardData.entries()).forEach(([key, value]) => {
            console.log(`${key}: ${value}`);
        });
        console.log('================');

        await createStandard(Number(laboratoryId), standardData);
        
        message.success('Стандартный образец успешно добавлен');
        onSuccess?.();
        onCancel();
    } catch (error) {
        if (error instanceof Error) {
            message.error(error.message);
        } else {
            message.error('Ошибка при добавлении стандартного образца');
        }
        console.error('Error:', error);
    } finally {
        setLoading(false);
    }
  };
  
  // Функция для рендеринга кастомных полей
  const renderCustomFields = () => {
    return customColumns.map(column => {
      const value = customFieldValues[column.key] || '';
      
      switch (column.type) {
        case 'text':
          return (
            <Col span={24} key={column.key}>
              <StyledText>{column.title}</StyledText>
              <StyledInput
                value={value}
                onChange={(e) => handleCustomFieldChange(column.key, e.target.value)}
                placeholder="введите значение"
              />
            </Col>
          );
        case 'int':
          return (
            <Col span={24} key={column.key}>
              <StyledText>{column.title}</StyledText>
              <StyledInput
                type="number"
                value={value}
                onChange={(e) => handleCustomFieldChange(column.key, e.target.value)}
                placeholder="введите число"
              />
            </Col>
          );
        case 'date':
          return (
            <Col span={24} key={column.key}>
              <StyledText>{column.title}</StyledText>
              <StyledDatePicker
                value={value ? dayjs(value) : null}
                onChange={(date: any) => {
                  if (date) {
                    handleCustomFieldChange(column.key, date.toISOString());
                  } else {
                    handleCustomFieldChange(column.key, null);
                  }
                }}
                style={{ width: '100%' }}
                format="DD.MM.YYYY"
                placeholder="Выберите дату"
              />
            </Col>
          );
        case 'boolean':
          return (
            <Col span={24} key={column.key}>
              <StyledText>{column.title}</StyledText>
              <CheckboxField
                title="Да"
                checked={Boolean(value)}
                onChange={(checked) => handleCustomFieldChange(column.key, checked)}
              />
            </Col>
          );
        default:
          return null;
      }
    });
  };

  return (
    <>
      <GlobalStyle />
      <ScreenContainer>
        <HeaderContainer>
          <ScreenTitle level={4} style={{ margin: 0 }}>Новая запись</ScreenTitle>
          <StyledLink onClick={() => {
            onCancel(); // Закрываем текущее окно
            setIsMultipleAddOpen(true); // Открываем окно множественного добавления
          }}>Добавить множество позиций</StyledLink>
        </HeaderContainer>
        
        <ScrollContent>
          <Row gutter={[16, 16]}>
            <Col span={24}>
              <StyledText>Наименование, тип, номер и категория СО</StyledText>
              <StyledAutoComplete
                value={name}
                onChange={(value: any) => {
                  console.log('AutoComplete value:', value);
                  if (typeof value === 'string') {
                    handleNameChange(value);
                  } else if (value?.value) {
                    handleNameChange(value.value);
                  }
                }}
                onSearch={handleSearch}
                options={nameOptions}
                placeholder="введите название"
                style={{ width: "100%" }}
              />
            </Col>

            <Col span={24}>
              <StyledText>Изготовитель СО</StyledText>
              <StyledInput
                value={manufacturer}
                onChange={(e) => setManufacturer(e.target.value)}
                placeholder="введите изготовителя"
              />
            </Col>

            <Col span={24}>
              <StyledText>Назначение</StyledText>
              <StyledInput
                value={purpose}
                onChange={(e) => setPurpose(e.target.value)}
                placeholder="введите назначение"
              />
            </Col>

            <Col span={24}>
              <StyledText>Шифр партии</StyledText>
              <StyledInput
                value={batchCode}
                onChange={(e) => setBatchCode(e.target.value)}
                placeholder="введите шифр партии"
              />
            </Col>

            <Col span={24}>
              <StyledText>Фасовка</StyledText>
              <StyledInput
                value={packaging}
                onChange={(e) => setPackaging(e.target.value)}
                placeholder="введите фасовку"
              />
            </Col>

            <Col span={24}>
              <StyledText>Показатель</StyledText>
              <StyledInput
                value={indicator}
                onChange={(e) => setIndicator(e.target.value)}
                placeholder="введите показатель"
              />
            </Col>

            <Col span={24}>
              <StyledText>Аттестованное значение м.д. %</StyledText>
              <StyledInput
                type="number"
                value={certifiedValue}
                onChange={(e) => setCertifiedValue(e.target.value)}
                placeholder="введите значение"
              />
            </Col>

            <Col span={24}>
              <StyledText>Единица измерения аттестованного значения</StyledText>
              <StyledInput
                value={certifiedValueUnit}
                onChange={(e) => setCertifiedValueUnit(e.target.value)}
                placeholder="введите единицу измерения"
              />
            </Col>

            <Col span={24}>
              <StyledText>Погрешность а.з. абс. %</StyledText>
              <StyledInput
                type="number"
                value={errorValue}
                onChange={(e) => setErrorValue(e.target.value)}
                placeholder="введите значение"
              />
            </Col>

            <Col span={24}>
              <StyledText>Единица измерения погрешности аттестованного значения</StyledText>
              <StyledInput
                value={errorValueUnit}
                onChange={(e) => setErrorValueUnit(e.target.value)}
                placeholder="введите единицу измерения"
              />
            </Col>

            <Col span={24}>
              <StyledText>Дополнительные сведения</StyledText>
              <StyledInput
                value={additionalInfo}
                onChange={(e) => setAdditionalInfo(e.target.value)}
                placeholder="введите информацию"
              />
            </Col>

            <Col span={24}>
              <StyledText>Нормативный документ</StyledText>
              <Row gutter={8} align="middle">
                <Col flex="auto">
                  <StyledInput
                    value={normativeDocument || 'Файл не выбран'}
                    readOnly
                    placeholder="Файл не выбран"
                  />
                </Col>
                <Col>
                  <Upload
                    accept=".pdf,.doc,.docx,.txt"
                    showUploadList={false}
                    beforeUpload={(file) => {
                      const maxSize = 20 * 1024 * 1024; // 20MB
                      if (file.size > maxSize) {
                        message.error('Размер файла не должен превышать 20MB');
                        return Upload.LIST_IGNORE;
                      }
                      setNormativeDocumentFile(file);
                      setNormativeDocument(file.name);
                      return false;
                    }}
                  >
                    <Button style={{ height: 40 }} icon={<UploadOutlined />}>Выбрать</Button>
                  </Upload>
                </Col>
                {normativeDocument && (
                  <Col>
                    <Button
                      style={{ height: 40 }}
                      icon={<DeleteOutlined />}
                      onClick={() => {
                        setNormativeDocumentFile(null);
                        setNormativeDocument('');
                      }}
                    />
                  </Col>
                )}
              </Row>
              <div style={{ fontSize: 12, color: '#8c8c8c', marginTop: 4 }}>
                До 20МБ. Форматы: PDF, DOC, DOCX, TXT
              </div>
            </Col>

            <Col span={24}>
              <StyledText>Срок годности экземпляра СО</StyledText>
              <StyledDatePicker
                value={expirationDate}
                onChange={(date: any) => setExpirationDate(date)}
                style={{ width: '100%' }}
                format="DD.MM.YYYY"
                placeholder="Выберите дату"
              />
            </Col>

            <Col span={24}>
              <StyledText>Уведомление об окончании срока годности</StyledText>
              <StyledInput
                value={expirationNotification}
                onChange={(e) => setExpirationNotification(e.target.value)}
                placeholder="введите уведомление"
              />
            </Col>

            <Col span={24}>
              <StyledText>Дата выпуска экземпляра СО</StyledText>
              <StyledDatePicker
                value={releaseDate}
                onChange={(date: any) => setReleaseDate(date)}
                style={{ width: '100%' }}
                format="DD.MM.YYYY"
                placeholder="Выберите дату"
              />
            </Col>

            <Col span={24}>
              <StyledText>Примечание</StyledText>
              <StyledInput
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                placeholder="введите примечание"
              />
            </Col>

            <Col span={24}>
              <StyledText>Место хранения</StyledText>
              <StyledInput
                value={storageLocation}
                onChange={(e) => setStorageLocation(e.target.value)}
                placeholder="введите место хранения"
              />
            </Col>

            <Col span={24}>
              <StyledText>Условия хранения</StyledText>
              <StyledInput
                value={storageConditions}
                onChange={(e) => setStorageConditions(e.target.value)}
                placeholder="введите условия хранения"
              />
            </Col>

            <Col span={24}>
              <StyledText>Срок хранения после вскрытия (дней)</StyledText>
              <StyledInput
                type="number"
                value={openedShelfLife}
                onChange={(e) => setOpenedShelfLife(e.target.value)}
                placeholder="введите срок хранения"
              />
            </Col>

            <Col span={24}>
              <StyledText>Дата вскрытия</StyledText>
              <StyledDatePicker
                value={openedDate}
                onChange={(date: unknown) => setOpenedDate(date as Dayjs | null)}
                format="DD.MM.YYYY"
                style={{ width: '100%' }}
                placeholder="Выберите дату"
              />
            </Col>

            <Col span={24}>
              <StyledText>Использовано</StyledText>
              <StyledInput
                type="number"
                value={used}
                onChange={(e) => setUsed(e.target.value)}
                placeholder="введите количество"
              />
            </Col>

            <Col span={24}>
              <StyledText>Остаток</StyledText>
              <StyledInput
                type="number"
                value={remaining}
                onChange={(e) => setRemaining(e.target.value)}
                placeholder="введите количество"
              />
            </Col>

            <Col span={24}>
              <StyledText>Минимальный остаток</StyledText>
              <StyledInput
                type="number"
                value={minimumRemaining}
                onChange={(e) => setMinimumRemaining(e.target.value)}
                placeholder="введите значение"
              />
            </Col>

            <Col span={24}>
              <StyledText>Прекурсор</StyledText>
              <CheckboxField
                title="Да"
                checked={isPrecursor}
                onChange={setIsPrecursor}
              />
            </Col>
            
            {/* Рендерим кастомные поля */}
            {renderCustomFields()}
          </Row>
        </ScrollContent>

        <BottomButtons>
          <ButtonsRow>
            <Button block style={{ width: 178 }} onClick={onCancel}>Отмена</Button>
            <Button
              type="primary"
              block
              style={{ width: 178 }}
              onClick={handleSave}
              loading={loading}
            >
              Сохранить
            </Button>
          </ButtonsRow>
        </BottomButtons>
      </ScreenContainer>

      <MultipleStandardSamplesAdd
        open={isMultipleAddOpen}
        onCancel={() => setIsMultipleAddOpen(false)}
        onSuccess={onSuccess}
      />
    </>
  );
};

// Добавляем глобальные стили
const GlobalStyle = createGlobalStyle`
  .custom-autocomplete {
    .ant-select-selector {
      height: 40px !important;
      padding: 4px 11px !important;
      background-color: #fafafa !important;
      border-radius: 4px !important;
    }
    
    .ant-select-selection-search-input {
      height: 38px !important;
    }
    
    .ant-select-selection-placeholder {
      line-height: 32px !important;
    }
  }
`;

export default Screen;

